import React, { useId } from 'react';
import { Box } from '@chakra-ui/react';
import { JobArchetype } from './jobArchetypes';
import type { GenderKey } from './jobArchetypes';

/**
 * Avatar "boneco" do colaborador — medalhão com profundidade (quase-3D):
 *   • disco com gradiente do arquétipo + brilho especular + vinheta interna;
 *   • silhueta (busto) variando por gênero (M / F / neutro);
 *   • anel externo colorido pelo status (Ativo/Demitido/Afastado);
 *   • emblema do trabalho (ícone phosphor) num badge no canto inferior direito;
 *   • arco de "tempo de casa" opcional (estilo XP) sobre o anel.
 *
 * Stateless e memoizável — pensado pra renderizar dezenas/centenas em grade.
 */

export type CollabStatus = 'ativo' | 'demitido' | 'afastado' | 'outro';

export function statusOf(status?: string | null, afastado?: boolean): CollabStatus {
  if (afastado) return 'afastado';
  const s = (status ?? '').toLowerCase();
  if (s.includes('ativ')) return 'ativo';
  if (s.includes('demit') || s.includes('inativ') || s.includes('deslig')) return 'demitido';
  if (s.includes('afast') || s.includes('feri') || s.includes('licen')) return 'afastado';
  return 'outro';
}

const STATUS_COLOR: Record<CollabStatus, string> = {
  ativo: '#16A34A',
  demitido: '#94A3B8',
  afastado: '#E08A1E',
  outro: '#6366F1',
};

/** Bustos por gênero (viewBox 0 0 100 100, centrado no disco). */
function Bust({ gender, fill }: { gender: GenderKey; fill: string }) {
  if (gender === 'F') {
    return (
      <g fill={fill}>
        {/* cabelo (atrás) */}
        <path
          d="M30 44c0-14 8-23 20-23s20 9 20 23c0 8-2 14-4 20l-5-3c2-6 3-11 3-17 0-10-6-16-14-16s-14 6-14 16c0 6 1 11 3 17l-5 3c-2-6-4-12-4-20z"
          opacity="0.55"
        />
        {/* cabeça */}
        <circle cx="50" cy="40" r="13" />
        {/* ombros */}
        <path d="M27 78c0-13 10-21 23-21s23 8 23 21v4H27z" />
      </g>
    );
  }
  if (gender === 'M') {
    return (
      <g fill={fill}>
        {/* cabelo curto (topo) */}
        <path d="M37 33c2-7 7-11 13-11s11 4 13 11c-3-3-8-5-13-5s-10 2-13 5z" opacity="0.6" />
        {/* cabeça */}
        <circle cx="50" cy="40" r="13" />
        {/* ombros (mais largos) */}
        <path d="M25 80c0-14 11-22 25-22s25 8 25 22v2H25z" />
      </g>
    );
  }
  // neutro
  return (
    <g fill={fill}>
      <circle cx="50" cy="40" r="13" />
      <path d="M26 79c0-13 11-21 24-21s24 8 24 21v3H26z" />
    </g>
  );
}

interface CollabAvatarProps {
  gender: GenderKey;
  archetype: JobArchetype;
  status?: CollabStatus;
  /** Anos de casa — desenha o arco de "level" sobre o anel (0 esconde). */
  anos?: number;
  size?: number;
}

const TWO_PI = Math.PI * 2;

export const CollabAvatar = React.memo(function CollabAvatar({
  gender,
  archetype,
  status = 'ativo',
  anos = 0,
  size = 72,
}: CollabAvatarProps) {
  const uid = useId().replace(/[:]/g, '');
  const [g0, g1] = archetype.gradient;
  const ring = STATUS_COLOR[status];
  const Emblem = archetype.Icon;

  // Arco de tempo de casa: satura em ~10 anos (cap visual).
  const R = 46;
  const frac = Math.max(0, Math.min(anos / 10, 1));
  const dash = frac * TWO_PI * R;
  const gap = TWO_PI * R;

  return (
    <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0}>
      <svg viewBox="0 0 100 100" width={size} height={size} role="img" aria-label={archetype.label}>
        <defs>
          <radialGradient id={`disc-${uid}`} cx="38%" cy="30%" r="80%">
            <stop offset="0%" stopColor={g0} />
            <stop offset="100%" stopColor={g1} />
          </radialGradient>
          <radialGradient id={`spec-${uid}`} cx="34%" cy="24%" r="42%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={`vig-${uid}`} cx="50%" cy="62%" r="62%">
            <stop offset="62%" stopColor="#000000" stopOpacity="0" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.22" />
          </radialGradient>
          <clipPath id={`clip-${uid}`}>
            <circle cx="50" cy="50" r="40" />
          </clipPath>
        </defs>

        {/* anel base (trilho do status) */}
        <circle
          cx="50"
          cy="50"
          r={R}
          fill="none"
          stroke={ring}
          strokeOpacity="0.22"
          strokeWidth="5"
        />
        {/* arco de tempo de casa */}
        {frac > 0 && (
          <circle
            cx="50"
            cy="50"
            r={R}
            fill="none"
            stroke={ring}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${gap}`}
            transform="rotate(-90 50 50)"
          />
        )}

        {/* disco */}
        <circle cx="50" cy="50" r="40" fill={`url(#disc-${uid})`} />
        <g clipPath={`url(#clip-${uid})`}>
          <Bust gender={gender} fill="#FFFFFF" />
          <rect x="10" y="10" width="80" height="80" fill={`url(#vig-${uid})`} />
          <ellipse cx="40" cy="32" rx="26" ry="20" fill={`url(#spec-${uid})`} />
        </g>
      </svg>

      {/* emblema do trabalho (badge canto inferior direito) */}
      <Box
        position="absolute"
        right="-2px"
        bottom="-2px"
        w={`${Math.round(size * 0.42)}px`}
        h={`${Math.round(size * 0.42)}px`}
        borderRadius="full"
        bg="white"
        boxShadow="0 3px 8px rgba(15,23,42,0.28)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="2px solid white"
      >
        <Box
          w="100%"
          h="100%"
          borderRadius="full"
          bgGradient={`linear(to-br, ${g0}, ${g1})`}
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="white"
        >
          <Emblem size={Math.round(size * 0.22)} weight="fill" />
        </Box>
      </Box>
    </Box>
  );
});
